/* ==========================================================================
   VyomTara | Collection Grid
   Standalone script. It only controls elements inside <vt-collection-grid>
   and its own quick view popup. It does not change any existing theme script.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements.get('vt-collection-grid')) return;

  var ICONS = {
    close: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>',
    next: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    star: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 1.4l2.63 5.33 5.87.86-4.25 4.14 1 5.85L10 14.82l-5.25 2.76 1-5.85L1.5 7.59l5.87-.86z"/></svg>'
  };

  function bool(value) {
    return value === 'true';
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  class VTCollectionGrid extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      var d = this.dataset;
      this.opts = {
        hideArrowsAtEnds: bool(d.hideArrowsEnds),
        afterAdd: d.afterAdd || 'stay',
        showRating: bool(d.showQvRating),
        showQuantity: bool(d.showQvQuantity),
        showVariants: bool(d.showQvVariants),
        showCompare: bool(d.showQvCompare),
        showCount: bool(d.showRatingCount),
        cartAddUrl: d.cartAddUrl || '/cart/add',
        cartUrl: d.cartUrl || '/cart',
        text: {
          add: d.textAdd || 'Add to cart',
          adding: d.textAdding || 'Adding...',
          added: d.textAdded || 'Added to cart',
          soldout: d.textSoldout || 'Sold out',
          unavailable: d.textUnavailable || 'Unavailable',
          error: d.textError || "Couldn't add to cart. Please try again.",
          details: d.textDetails || 'View full details',
          quantity: d.textQuantity || 'Quantity'
        }
      };

      this.carousel = this.querySelector('[data-vt-carousel]');
      this.tabs = toArray(this.querySelectorAll('[data-vt-tab]'));
      this.panels = toArray(this.querySelectorAll('[data-vt-panel]'));
      this.prevBtn = this.querySelector('[data-vt-prev]');
      this.nextBtn = this.querySelector('[data-vt-next]');
      this.activeIndex = 0;
      this.isOpen = false;

      this.bindTabs();
      this.bindArrows();
      this.bindQuickView();

      var self = this;
      this.onResize = function () { self.updateArrows(); };
      window.addEventListener('resize', this.onResize);

      this.onBlockSelect = function (event) {
        if (!event.detail || !event.detail.blockId) return;
        var panel = self.querySelector('[data-vt-panel][data-block-id="' + event.detail.blockId + '"]');
        if (panel) self.activate(parseInt(panel.dataset.vtPanel, 10));
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.updateArrows();
      window.setTimeout(function () { self.updateArrows(); }, 500);
      window.addEventListener('load', this.onResize, { once: true });
    }

    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.modal) {
        if (this.isOpen) this.closeModal(true);
        this.modal.remove();
        this.modal = null;
      }
      this._ready = false;
    }

    /* ---------- Tabs ---------- */
    bindTabs() {
      var self = this;
      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          self.activate(parseInt(tab.dataset.vtTab, 10));
        });
        tab.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          var dir = event.key === 'ArrowRight' ? 1 : -1;
          var next = (self.activeIndex + dir + self.tabs.length) % self.tabs.length;
          self.activate(next);
          self.tabs[next].focus();
        });
      });
    }

    activate(index) {
      if (!this.panels[index]) return;
      this.activeIndex = index;
      this.panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });
      this.tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      var track = this.getTrack();
      if (track) track.scrollLeft = 0;
      this.updateArrows();
    }

    getTrack() {
      var panel = this.panels[this.activeIndex];
      return panel ? panel.querySelector('[data-vt-track]') : null;
    }

    /* ---------- Arrows ---------- */
    bindArrows() {
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.scrollPage(-1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.scrollPage(1); });

      this.panels.forEach(function (panel) {
        var track = panel.querySelector('[data-vt-track]');
        if (!track) return;
        track.addEventListener('scroll', function () {
          if (self._raf) return;
          self._raf = window.requestAnimationFrame(function () {
            self._raf = null;
            self.updateArrows();
          });
        }, { passive: true });
      });
    }

    scrollPage(direction) {
      var track = this.getTrack();
      if (!track) return;
      var card = track.querySelector('.vt-cg__card');
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      var step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      var perView = Math.max(1, Math.floor((track.clientWidth + gap) / step));
      track.scrollBy({ left: direction * step * perView, behavior: 'smooth' });
    }

    updateArrows() {
      var track = this.getTrack();
      if (!track || !this.carousel) return;

      var max = track.scrollWidth - track.clientWidth;
      this.carousel.classList.toggle('is-static', max <= 4);

      var media = track.querySelector('.vt-cg__media-wrap');
      if (media) {
        var top = media.getBoundingClientRect().top - this.carousel.getBoundingClientRect().top;
        this.carousel.style.setProperty('--vt-cg-arrow-top', Math.round(top + media.offsetHeight / 2) + 'px');
      }

      var atStart = track.scrollLeft <= 4;
      var atEnd = track.scrollLeft >= max - 4;
      var cls = this.opts.hideArrowsAtEnds ? 'is-hidden' : 'is-dimmed';
      if (this.prevBtn) {
        this.prevBtn.classList.toggle(cls, atStart);
        this.prevBtn.disabled = atStart;
      }
      if (this.nextBtn) {
        this.nextBtn.classList.toggle(cls, atEnd);
        this.nextBtn.disabled = atEnd;
      }
    }

    /* ---------- Quick view ---------- */
    bindQuickView() {
      var self = this;
      this.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-vt-qv]');
        if (!btn || !self.contains(btn)) return;
        var card = btn.closest('[data-vt-card]');
        var dataEl = card && card.querySelector('[data-vt-product]');
        if (!dataEl) return;
        event.preventDefault();
        var data;
        try {
          data = JSON.parse(dataEl.textContent);
        } catch (e) {
          var link = card.querySelector('a[href]');
          if (link) window.location.href = link.href;
          return;
        }
        self.openModal(data, btn);
      });
    }

    buildModal() {
      if (this.modal) return;
      var self = this;
      var wrapper = this.closest('.vt-cg');
      var modal = document.createElement('div');
      modal.className = 'vt-cg-modal';
      if (this.classList.contains('vt-cg--arrows-circle')) modal.classList.add('vt-cg-modal--circle');
      modal.hidden = true;
      if (wrapper && wrapper.getAttribute('style')) modal.setAttribute('style', wrapper.getAttribute('style'));

      modal.innerHTML =
        '<div class="vt-cg-modal__overlay" data-close></div>' +
        '<div class="vt-cg-modal__panel" role="dialog" aria-modal="true" aria-labelledby="' + this.dataset.sectionId + '-qv-title">' +
          '<button type="button" class="vt-cg-modal__close" data-close aria-label="Close">' + ICONS.close + '</button>' +
          '<div class="vt-cg-modal__gallery">' +
            '<div class="vt-cg-modal__main">' +
              '<img data-main alt="">' +
              '<button type="button" class="vt-cg-modal__gnav vt-cg-modal__gnav--prev" data-gprev aria-label="Previous image">' + ICONS.prev + '</button>' +
              '<button type="button" class="vt-cg-modal__gnav vt-cg-modal__gnav--next" data-gnext aria-label="Next image">' + ICONS.next + '</button>' +
            '</div>' +
            '<div class="vt-cg-modal__thumbs" data-thumbs></div>' +
          '</div>' +
          '<div class="vt-cg-modal__details">' +
            '<h2 class="vt-cg-modal__title" id="' + this.dataset.sectionId + '-qv-title" data-title></h2>' +
            '<div class="vt-cg-modal__rating" data-rating></div>' +
            '<p class="vt-cg-modal__price"><span data-price></span><s class="vt-cg-modal__compare" data-compare></s></p>' +
            '<div class="vt-cg-modal__options" data-options></div>' +
            '<div data-qty-wrap>' +
              '<p class="vt-cg-modal__qty-label" data-qty-label></p>' +
              '<div class="vt-cg-modal__qty">' +
                '<button type="button" data-qty-minus aria-label="Decrease quantity">&minus;</button>' +
                '<input type="number" min="1" value="1" inputmode="numeric" data-qty aria-label="Quantity">' +
                '<button type="button" data-qty-plus aria-label="Increase quantity">+</button>' +
              '</div>' +
            '</div>' +
            '<button type="button" class="vt-cg-modal__atc" data-atc></button>' +
            '<p class="vt-cg-modal__msg" data-msg role="status" aria-live="polite"></p>' +
            '<a class="vt-cg-modal__details-link" data-details href="#"></a>' +
          '</div>' +
        '</div>';

      document.body.appendChild(modal);
      this.modal = modal;

      var q = function (sel) { return modal.querySelector(sel); };
      this.el = {
        main: q('[data-main]'),
        gprev: q('[data-gprev]'),
        gnext: q('[data-gnext]'),
        thumbs: q('[data-thumbs]'),
        title: q('[data-title]'),
        rating: q('[data-rating]'),
        price: q('[data-price]'),
        compare: q('[data-compare]'),
        options: q('[data-options]'),
        qtyWrap: q('[data-qty-wrap]'),
        qtyLabel: q('[data-qty-label]'),
        qty: q('[data-qty]'),
        atc: q('[data-atc]'),
        msg: q('[data-msg]'),
        details: q('[data-details]')
      };

      this.el.qtyLabel.textContent = this.opts.text.quantity;
      this.el.details.textContent = this.opts.text.details;
      if (!this.opts.showQuantity) this.el.qtyWrap.hidden = true;

      modal.addEventListener('click', function (event) {
        var t = event.target;
        if (t.closest('[data-close]')) { self.closeModal(); return; }
        if (t.closest('[data-gprev]')) { self.showImage(self.imageIndex - 1); return; }
        if (t.closest('[data-gnext]')) { self.showImage(self.imageIndex + 1); return; }
        var thumb = t.closest('[data-thumb]');
        if (thumb) { self.showImage(parseInt(thumb.dataset.thumb, 10)); return; }
        var opt = t.closest('[data-opt-index]');
        if (opt) { self.selectOption(opt); return; }
        if (t.closest('[data-qty-minus]')) { self.changeQty(-1); return; }
        if (t.closest('[data-qty-plus]')) { self.changeQty(1); return; }
        if (t.closest('[data-atc]')) { self.addToCart(); }
      });

      this.el.qty.addEventListener('change', function () { self.changeQty(0); });

      var main = this.el.main.parentNode;
      main.addEventListener('touchstart', function (event) {
        self._tx = event.touches[0].clientX;
      }, { passive: true });
      main.addEventListener('touchend', function (event) {
        if (self._tx == null) return;
        var dx = event.changedTouches[0].clientX - self._tx;
        self._tx = null;
        if (Math.abs(dx) > 40) self.showImage(self.imageIndex + (dx < 0 ? 1 : -1));
      }, { passive: true });

      this.onKey = function (event) {
        if (!self.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          self.closeModal();
        } else if (event.key === 'Tab') {
          self.trapFocus(event);
        }
      };
    }

    openModal(product, trigger) {
      this.buildModal();
      this.product = product;
      this.lastFocus = trigger || document.activeElement;
      this.isOpen = true;

      var el = this.el;
      el.title.textContent = product.title;
      el.details.href = product.url;
      el.msg.textContent = '';
      el.msg.classList.remove('is-error');
      el.qty.value = 1;

      this.renderRating(product);
      this.renderGallery(product.images || []);

      var first = product.variants.filter(function (v) { return v.id === product.selected; })[0] ||
        product.variants.filter(function (v) { return v.available; })[0] ||
        product.variants[0];
      this.selected = first ? first.options.slice() : [];
      this.renderOptions();
      this.updateVariant(true);

      this.modal.hidden = false;
      document.documentElement.classList.add('vt-cg-lock');
      document.addEventListener('keydown', this.onKey);

      var closeBtn = this.modal.querySelector('.vt-cg-modal__close');
      window.requestAnimationFrame(function () {
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      });
    }

    closeModal(silent) {
      if (!this.isOpen) return;
      this.isOpen = false;
      window.clearTimeout(this._addedTimer);
      if (this.modal) this.modal.hidden = true;
      document.documentElement.classList.remove('vt-cg-lock');
      document.removeEventListener('keydown', this.onKey);
      if (silent) return;
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus({ preventScroll: true });
      }
    }

    trapFocus(event) {
      var focusable = toArray(this.modal.querySelectorAll('button:not([disabled]), a[href], input, [tabindex]'))
        .filter(function (el) { return el.tabIndex >= 0 && el.getClientRects().length > 0; });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.modal.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    renderRating(product) {
      var wrap = this.el.rating;
      wrap.innerHTML = '';
      var rating = parseFloat(product.rating);
      if (!this.opts.showRating || !rating) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      var stars = ICONS.star + ICONS.star + ICONS.star + ICONS.star + ICONS.star;
      var pct = Math.max(0, Math.min(100, rating * 20));
      wrap.innerHTML =
        '<span class="vt-cg__stars" style="--vt-cg-rating:' + pct + '%" role="img" aria-label="Rated ' + rating + ' out of 5">' +
          '<span class="vt-cg__stars-empty">' + stars + '</span>' +
          '<span class="vt-cg__stars-fill">' + stars + '</span>' +
        '</span>';
      var value = document.createElement('span');
      value.textContent = String(product.rating) + (this.opts.showCount && product.count ? ' (' + product.count + ')' : '');
      wrap.appendChild(value);
    }

    /* ---------- Gallery ---------- */
    renderGallery(images) {
      var self = this;
      this.images = images;
      this.imageIndex = 0;
      this.el.thumbs.innerHTML = '';

      images.forEach(function (src, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vt-cg-modal__thumb';
        btn.dataset.thumb = String(i);
        btn.setAttribute('aria-label', 'Show image ' + (i + 1));
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        btn.appendChild(img);
        self.el.thumbs.appendChild(btn);
      });

      var multiple = images.length > 1;
      this.el.thumbs.hidden = !multiple;
      this.el.gprev.hidden = !multiple;
      this.el.gnext.hidden = !multiple;
      this.showImage(0);
    }

    showImage(index) {
      var count = this.images ? this.images.length : 0;
      if (!count) {
        this.el.main.removeAttribute('src');
        this.el.main.hidden = true;
        return;
      }
      this.imageIndex = ((index % count) + count) % count;
      this.el.main.hidden = false;
      this.el.main.src = this.images[this.imageIndex];
      this.el.main.alt = this.product ? this.product.title : '';
      var current = this.imageIndex;
      toArray(this.el.thumbs.children).forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === current);
      });
    }

    /* ---------- Variants ---------- */
    renderOptions() {
      var wrap = this.el.options;
      var product = this.product;
      wrap.innerHTML = '';

      if (!this.opts.showVariants || product.hasOnlyDefault || !product.options || !product.options.length) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;

      product.options.forEach(function (name, i) {
        var values = [];
        product.variants.forEach(function (variant) {
          var value = variant.options[i];
          if (value != null && values.indexOf(value) === -1) values.push(value);
        });

        var group = document.createElement('div');
        group.setAttribute('role', 'radiogroup');
        group.setAttribute('aria-label', name);

        var label = document.createElement('p');
        label.className = 'vt-cg-modal__opt-name';
        label.dataset.optName = name;
        label.textContent = name;

        var list = document.createElement('div');
        list.className = 'vt-cg-modal__values';

        values.forEach(function (value) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'vt-cg-modal__value';
          btn.textContent = value;
          btn.dataset.optIndex = String(i);
          btn.dataset.optValue = value;
          btn.setAttribute('role', 'radio');
          list.appendChild(btn);
        });

        group.appendChild(label);
        group.appendChild(list);
        wrap.appendChild(group);
      });
    }

    selectOption(button) {
      this.selected[parseInt(button.dataset.optIndex, 10)] = button.dataset.optValue;
      this.el.msg.textContent = '';
      this.el.msg.classList.remove('is-error');
      this.updateVariant(false);
    }

    updateVariant(initial) {
      var self = this;
      var product = this.product;
      var variant = product.variants.filter(function (v) {
        return v.options.every(function (option, i) { return option === self.selected[i]; });
      })[0] || null;
      this.variant = variant;

      toArray(this.el.options.querySelectorAll('[data-opt-index]')).forEach(function (btn) {
        var i = parseInt(btn.dataset.optIndex, 10);
        var value = btn.dataset.optValue;
        var isSelected = self.selected[i] === value;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        var possible = product.variants.some(function (v) {
          return v.available && v.options[i] === value && v.options.every(function (option, j) {
            return j === i || option === self.selected[j];
          });
        });
        btn.classList.toggle('is-unavailable', !possible);
      });

      toArray(this.el.options.querySelectorAll('[data-opt-name]')).forEach(function (label, i) {
        label.textContent = label.dataset.optName + ': ' + (self.selected[i] || '');
      });

      var source = variant || product.variants[0];
      this.el.price.textContent = source ? source.price : '';
      if (this.opts.showCompare && source && source.compare) {
        this.el.compare.textContent = source.compare;
        this.el.compare.hidden = false;
      } else {
        this.el.compare.textContent = '';
        this.el.compare.hidden = true;
      }

      if (!initial && variant && variant.image && this.images) {
        var idx = this.images.indexOf(variant.image);
        if (idx > -1) this.showImage(idx);
      }

      if (variant) {
        var url = product.url + (product.url.indexOf('?') > -1 ? '&' : '?') + 'variant=' + variant.id;
        this.el.details.href = url;
      }

      if (!variant) this.setAtcState('unavailable');
      else if (!variant.available) this.setAtcState('soldout');
      else this.setAtcState('ready');
    }

    changeQty(delta) {
      var value = parseInt(this.el.qty.value, 10) || 1;
      value = Math.max(1, Math.min(99, value + delta));
      this.el.qty.value = value;
    }

    setAtcState(state) {
      var btn = this.el.atc;
      var t = this.opts.text;
      btn.disabled = false;
      switch (state) {
        case 'adding':
          btn.textContent = t.adding;
          btn.disabled = true;
          break;
        case 'added':
          btn.textContent = t.added;
          break;
        case 'soldout':
          btn.textContent = t.soldout;
          btn.disabled = true;
          break;
        case 'unavailable':
          btn.textContent = t.unavailable;
          btn.disabled = true;
          break;
        default:
          btn.textContent = t.add;
      }
    }

    /* ---------- Cart ---------- */
    addToCart() {
      var self = this;
      var variant = this.variant;
      if (!variant || !variant.available || this._adding) return;

      this._adding = true;
      this.setAtcState('adding');
      this.el.msg.textContent = '';
      this.el.msg.classList.remove('is-error');

      var quantity = this.opts.showQuantity ? Math.max(1, parseInt(this.el.qty.value, 10) || 1) : 1;
      var drawer = document.querySelector('cart-drawer');
      var useDrawer = this.opts.afterAdd === 'drawer' && drawer && typeof drawer.renderContents === 'function';
      var sections = ['cart-icon-bubble'];
      if (useDrawer) sections.push('cart-drawer');

      fetch(this.opts.cartAddUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variant.id,
          quantity: quantity,
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok || data.status) {
              throw new Error(data.description || data.message || self.opts.text.error);
            }
            return data;
          });
        })
        .then(function (data) {
          self.updateCartBubble(data.sections);

          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vt-collection-grid', productVariantId: variant.id, cartData: data });
            }
          } catch (e) {}
          document.dispatchEvent(new CustomEvent('vt-cg:added', { detail: { variantId: variant.id, quantity: quantity, item: data } }));

          if (self.opts.afterAdd === 'cart') {
            window.location.href = self.opts.cartUrl;
            return;
          }

          if (useDrawer) {
            self.closeModal();
            try {
              drawer.classList.remove('is-empty');
              drawer.renderContents(data);
            } catch (e) {
              window.location.href = self.opts.cartUrl;
            }
            return;
          }

          self.setAtcState('added');
          window.clearTimeout(self._addedTimer);
          self._addedTimer = window.setTimeout(function () {
            if (self.variant === variant) self.updateVariant(true);
          }, 2200);
        })
        .catch(function (error) {
          self.el.msg.textContent = (error && error.message) || self.opts.text.error;
          self.el.msg.classList.add('is-error');
          self.updateVariant(true);
        })
        .then(function () {
          self._adding = false;
        });
    }

    updateCartBubble(sections) {
      if (!sections || !sections['cart-icon-bubble']) return;
      var bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      var doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var inner = doc.querySelector('.shopify-section');
      bubble.innerHTML = inner ? inner.innerHTML : doc.body.innerHTML;
    }
  }

  window.customElements.define('vt-collection-grid', VTCollectionGrid);
})();
